import React, { useState } from "react";
import {
  Button,
  Dialog,
  DialogActions,
  DialogContent,
  DialogContentText,
  DialogTitle,
  TextField,
  makeStyles,
} from "@material-ui/core";
import { useTranslation } from "react-i18next";
import { resetPasswordUrl } from "../../common/infrastructure/endpoints";

interface IResetPasswordProps {
  open: boolean;
  onClose: () => void;
  domainCode: string;
}

interface IResetPasswordBody {
  ObjectNumber: string;
  DomainCode: string;
  Email: string;
}

const useStyles = makeStyles({
  paper: {
    minWidth: "100%",
    position: "absolute",
    bottom: 0,
    margin: 0,
    borderRadius: 0,
  },
});

export const ResetPassword: React.FC<IResetPasswordProps> = ({
  open,
  onClose,
  domainCode,
}) => {
  const { t } = useTranslation();
  const classes = useStyles();
  const [objectNumber, setObjectNumber] = useState("");
  const [email, setEmail] = useState("");
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [loading, setLoading] = useState(false);

  const handleSubmit = async () => {
    if (!objectNumber || !email || !email.includes("@")) {
      setError(t("Please fill in all fields"));
      return;
    }
    setError("");
    setLoading(true);
    try {
      const resp = await fetch(resetPasswordUrl, {
        method: "POST",
        headers: { "Content-Type": "application/json" },
        body: JSON.stringify({
          ObjectNumber: objectNumber,
          DomainCode: domainCode,
          Email: email,
        } as IResetPasswordBody),
      });
      if (resp.ok) {
        setSuccess(true);
      } else {
        setError(t("Something went wrong. Please try again."));
      }
    } catch {
      setError(t("Something went wrong. Please try again."));
    } finally {
      setLoading(false);
    }
  };

  const handleClose = () => {
    setObjectNumber("");
    setEmail("");
    setError("");
    setSuccess(false);
    onClose();
  };

  return (
    <Dialog
      open={open}
      onClose={handleClose}
      classes={{ paper: classes.paper }}
    >
      <DialogContent>
        <DialogTitle>{t("Reset password")}</DialogTitle>
        {success ? (
          <DialogContentText color="primary">
            {t(
              "If the information is correct, a new password has been sent to your email address."
            )}
          </DialogContentText>
        ) : (
          <>
            <DialogContentText color="primary">
              {t(
                "Enter your object number and email address to receive a new password."
              )}
            </DialogContentText>
            <TextField
              fullWidth
              variant="outlined"
              label={t("Object-nr")}
              value={objectNumber}
              onChange={(e) => setObjectNumber(e.target.value)}
              style={{ marginBottom: 12 }}
            />
            <TextField
              fullWidth
              variant="outlined"
              label={t("Email")}
              type="email"
              value={email}
              onChange={(e) => setEmail(e.target.value)}
              error={!!error}
              helperText={error}
            />
          </>
        )}
      </DialogContent>
      <DialogActions>
        {success ? (
          <Button
            fullWidth
            variant="contained"
            color="secondary"
            onClick={handleClose}
          >
            {t("Close")}
          </Button>
        ) : (
          <>
            <Button onClick={handleClose} color="secondary" variant="text">
              {t("Cancel")}
            </Button>
            <Button
              onClick={handleSubmit}
              color="primary"
              variant="contained"
              disabled={loading || !objectNumber || !email}
            >
              {t("Reset password")}
            </Button>
          </>
        )}
      </DialogActions>
    </Dialog>
  );
};
